import { LAB_COUNT, BRAND_COUNT_LABEL, SKU_COUNT_SHORT } from "@/data/companyFacts";
// Single source of truth for per-route SEO metadata.
//
// Tags are emitted server-side through TanStack Router's head() API, never
// through a React component, so crawlers always receive exactly one of each
// tag in the server-rendered HTML.

const SITE_URL = "https://www.goptichub.com";
const DEFAULT_OG_IMAGE = "https://www.goptichub.com/og-image.png";

// Per-route titles/descriptions.
export const ROUTE_META: Record<string, { title: string; description: string }> = {
  "/": {
    title: "GoHub – Add Prescription Eyewear to Your Online Store",
    description:
      `GoHub is the plug-and-play platform to add prescription eyewear to your online store. ${BRAND_COUNT_LABEL} brands, ${SKU_COUNT_SHORT} frames, AI optician, VTO, ${LAB_COUNT}-lab EU/US fulfillment.`,
  },
  "/virtual-try-on": {
    title: "Eyewear Virtual Try-On & PD Measurement API | GoHub",
    description:
      "AR virtual try-on and AI pupillary distance measurement for eyewear stores. No app, 3 product photos, up to 40% higher conversion. Built for e-commerce.",
  },
  "/pharmacy": {
    title: "Sell Prescription Eyewear in Your Online Pharmacy | GoHub",
    description:
      "Sell prescription eyewear from your pharmacy website. Plug-and-play catalog, VTO, and lab fulfillment by GoHub.",
  },
  "/brands-resellers": {
    title: "Add Prescription Lenses to Your Frame Store | GoHub",
    description:
      "Eyewear brands and resellers: sell the frames you already list as complete prescription pairs. No lab, no inventory, no optical staff. Fulfilled under your brand.",
  },
  "/opticians": {
    title: "Online Prescription Eyewear for Optical Practices | GoHub",
    description:
      "Opticians: extend your practice online with a 17,000 frame catalog, virtual try-on, PD measurement and certified lab fulfillment. White label, live in days.",
  },

  "/dropshipping": {
    title: "Prescription Eyewear Dropshipping, No Inventory | GoHub",
    description:
      "Dropship prescription glasses, not just frames. GoHub adds Rx lenses, virtual try-on and certified lab fulfillment to your store. Zero inventory, live in days.",
  },

  "/blog": {
    title: "Eyewear E-Commerce Insights & Guides | GoHub Blog",
    description:
      "Industry insights on selling prescription eyewear online, supply chain innovation, virtual try-on technology, and retail expansion strategies.",
  },
  "/about": {
    title: "About GoHub | The B2B Prescription Eyewear Platform",
    description:
      `GoHub is a B2B prescription eyewear platform connecting online retailers to ${LAB_COUNT} certified optical labs across the EU and US.`,
  },
  "/contact": {
    title: "Contact GoHub | Get in Touch with Our Team",
    description:
      "Contact GoHub for partnerships, demos, and integration questions on our B2B prescription eyewear platform. We reply within one business day.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | GoHub Prescription Eyewear Platform",
    description:
      "Read how GoHub collects, processes, stores, and protects personal and prescription data. GDPR, HIPAA-aligned, and CE-compliant by design.",
  },
  "/terms-of-use": {
    title: "Terms of Use | GoHub Prescription Eyewear Platform",
    description:
      "Review the terms and conditions that govern the use of the GoHub platform, services, APIs, and website by retailers, brands, and end customers.",
  },
  "/cookie-policy": {
    title: "Cookie Policy – GoHub",
    description: "How GoHub uses cookies on goptichub.com.",
  },
  "/wizard-consumer-terms": {
    title: "Optical Wizard Consumer Terms | GoHub",
    description:
      'By clicking "add lens", you have opened the GoHub Optical Wizard (the "Wizard"), a lens selection service provided by Global Optic Hub LLC.',
  },
};

// Per-route Open Graph images. Routes not listed here fall back to
// DEFAULT_OG_IMAGE. Paths are resolved to absolute URLs by buildHead().
export const ROUTE_OG_IMAGE: Record<string, string> = {
  "/opticians": "/og/opticians.png",
  "/brands-resellers": "/og/brands-resellers.png",
  "/virtual-try-on": "/og/virtual-try-on.png",
  "/pharmacy": "/og/pharmacy.png",
  "/dropshipping": "/og/dropshipping.png",
};



export interface BreadcrumbItem {
  name: string;
  url: string;
}

type JsonLd = Record<string, unknown>;

export interface BuildHeadOptions {
  /** Route path, e.g. "/pharmacy". Used for ROUTE_META lookup + canonical. */
  path?: string;
  title?: string | undefined;
  description?: string | undefined;
  canonical?: string | undefined;
  ogImage?: string | undefined;
  ogType?: string | undefined;
  twitterCard?: string | undefined;
  noindex?: boolean | undefined;
  jsonLd?: JsonLd | JsonLd[] | undefined;
  breadcrumbs?: BreadcrumbItem[] | undefined;
  links?: Array<Record<string, string>> | undefined;
}

/** Converts a relative path or partial URL into an absolute https URL. */
export const absoluteUrl = (input?: string | null): string | undefined => {
  if (!input) return undefined;
  const value = input.trim();
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("//")) return `https:${value}`;
  return `${SITE_URL}${value.startsWith("/") ? "" : "/"}${value}`;
};

/**
 * Builds the `{ meta, links, scripts }` object returned by a route's head().
 * Emits exactly one of each SEO tag.
 */
export const buildHead = ({
  path,
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  twitterCard = "summary_large_image",
  noindex,
  jsonLd,
  breadcrumbs,
  links,
}: BuildHeadOptions = {}) => {
  const normalizedPath = path ? path.replace(/\/$/, "") || "/" : undefined;
  const fallback = normalizedPath ? ROUTE_META[normalizedPath] : undefined;

  const resolvedTitle = title || fallback?.title;
  const resolvedDescription = description || fallback?.description;
  const resolvedCanonical =
    canonical ||
    (normalizedPath ? `${SITE_URL}${normalizedPath === "/" ? "" : normalizedPath}` : SITE_URL);
  const resolvedOgImage =
    absoluteUrl(ogImage) ||
    absoluteUrl(normalizedPath ? ROUTE_OG_IMAGE[normalizedPath] : undefined) ||
    DEFAULT_OG_IMAGE;


  const breadcrumbJsonLd = breadcrumbs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: item.name,
          item: item.url,
        })),
      }
    : null;

  const allJsonLd = [
    ...(jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []),
    ...(breadcrumbJsonLd ? [breadcrumbJsonLd] : []),
  ];

  const meta: Array<Record<string, string>> = [];
  if (resolvedTitle) meta.push({ title: resolvedTitle });
  if (resolvedDescription) meta.push({ name: "description", content: resolvedDescription });
  meta.push({
    name: "robots",
    content: noindex
      ? "noindex, nofollow"
      : "max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  });
  if (resolvedTitle) meta.push({ property: "og:title", content: resolvedTitle });
  if (resolvedDescription) meta.push({ property: "og:description", content: resolvedDescription });
  meta.push({ property: "og:type", content: ogType });
  meta.push({ property: "og:url", content: resolvedCanonical });
  meta.push({ property: "og:image", content: resolvedOgImage });
  meta.push({ name: "twitter:card", content: twitterCard });
  if (resolvedTitle) meta.push({ name: "twitter:title", content: resolvedTitle });
  if (resolvedDescription) meta.push({ name: "twitter:description", content: resolvedDescription });
  meta.push({ name: "twitter:image", content: resolvedOgImage });

  return {
    meta,
    links: [{ rel: "canonical", href: resolvedCanonical }, ...(links ?? [])],
    scripts: allJsonLd.map((ld) => ({
      type: "application/ld+json",
      children: JSON.stringify(ld),
    })),
  };
};

export { SITE_URL, DEFAULT_OG_IMAGE };
